import { Component, OnInit } from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { FileDownloadService } from 'src/app/shared/file-download/file-download.service';
import { config } from 'src/config/config';
import { getEnv } from 'src/modules/utility';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { EcmService } from '../../../ecm.service';
import { Observable } from 'rxjs';
import { map, startWith, tap } from 'rxjs/operators';
import { CgpSnackbarService } from 'src/app/shared/cgp-snackbar/cgp-snackbar.service';

interface Program {
    id: number;
    programNameYear: string;
}

@Component({
    // tslint:disable-next-line: component-selector
    selector: 'ecm-parts-reports',
    templateUrl: './ecm-parts-reports.component.html',
    styleUrls: ['./ecm-parts-reports.component.scss']
})
export class EcmPartsReportsComponent implements OnInit {

    partsReportForm: UntypedFormGroup;
    programs: Program[];
    filteredPrograms: Observable<Program[]>;
    authNumbers: string[];
    programNames: any;
    
    constructor(
        private readonly dialogRef: MatDialogRef<EcmPartsReportsComponent>,
        private readonly fb: UntypedFormBuilder,
        private readonly fileDownloadService: FileDownloadService,
        private readonly ecmService: EcmService,
        private readonly snackbar: CgpSnackbarService
    ) {
        const now = new Date();
        const yearAgo = new Date();
        yearAgo.setFullYear((yearAgo.getFullYear() - 1));

        this.partsReportForm = this.fb.group({
            authNumber: [],
            program: [],
            startApproval: [yearAgo],
            endApproval: [now],
        });
    }

    ngOnInit() {
        const ctrl = this.partsReportForm.controls;
        ctrl['authNumber'].disable();
        ctrl['program'].disable();
        ctrl['startApproval'].disable();
        ctrl['endApproval'].disable();
       
        this.ecmService.getAllProgramNames().pipe(
            tap((res) => {
                if (res) {
                    this.programs = res;
                    this.programNames = (this.programs.filter(p => p.programNameYear != null)).map(p => p.programNameYear);
                }
            }),
            tap(() => {
                this.filteredPrograms = this.partsReportForm.controls.program.valueChanges
                    .pipe(
                        startWith(''),
                        map(value => {
                            const filterVal = (typeof value === 'string') ? value.toLowerCase() : value.programNameYear.toLowerCase();
                            return this.programs.filter(p => p.programNameYear.toLowerCase().indexOf(filterVal) === 0);
                        })
                    );
            })
        ).subscribe();

        this.ecmService.getAuthorityNumbers()
            .subscribe(authNumbers => this.authNumbers = authNumbers);
    }

    displayProgram(program: Program): string {
        return program && program.programNameYear ? program.programNameYear : '';
    }

    downloadReport(url: string, body: any, filename: string) {
        const ref = this.snackbar.showSnackbar({
            message: 'Downloading...',
            action: 'Dismiss',
            duration: null
        });

        this.fileDownloadService.downloadExcel(url, body, { filename: this.fileName(filename) })
            .subscribe(() => ref.dismiss());
    }

    onPartsListClick() {
        this.downloadReport(`${config.tprecmApiBaseUrl[getEnv()]}ecm/report/part-list/download`, this.formBody, 'PartsList');
    }

    onDetailedPartsListClick() {
        this.downloadReport(`${config.tprecmApiBaseUrl[getEnv()]}ecm/report/detail-part-list/download`, this.formBody, 'DetailPartsList');
    }

    onDetailedPartsListDesignGDTClick() {
        this.downloadReport(`${config.tprecmApiBaseUrl[getEnv()]}ecm/report/detail-part-list-design-gdt/download`, this.formBody, 'DetailPartsListDesignGDT');
    }

    onToolPartListClick() {
        this.downloadReport(`${config.tprecmApiBaseUrl[getEnv()]}ecm/report/tool-part-list/download`, this.formBody, 'ToolPartsList');
    }

    toggleDatesField() {
        const ctrlStartApproval = this.partsReportForm.controls['startApproval'];
        const ctrlEndApproval = this.partsReportForm.controls['endApproval'];

        const statusStartApproval = ctrlStartApproval.disabled;
        (statusStartApproval === true) ? ctrlStartApproval.enable() : ctrlStartApproval.disable();

        const statusEndApproval = ctrlEndApproval.disabled;
        (statusEndApproval === true) ? ctrlEndApproval.enable() : ctrlEndApproval.disable();
    }

    toggleProgramField() {
        const ctrl = this.partsReportForm.controls['program'];
        const statusProgram = ctrl.disabled;
        (statusProgram === true) ? ctrl.enable() : ctrl.disable();
    }

    toggleAuthNumberField() {
        const ctrl = this.partsReportForm.controls['authNumber'];
        const statusAuthNumber = ctrl.disabled;
        (statusAuthNumber === true) ? ctrl.enable() : ctrl.disable();
    }

    onCloseClick() {
        this.dialogRef.close();
    }

    get formBody() {
        const body = this.partsReportForm.value;
        return {
            authNumber: body.authNumber,
            programId: body.program ? body.program.id : body.program,
            startApproval: body.startApproval,
            endApproval: body.endApproval
        };
    }

    fileName(base: string): string {
        const form = this.partsReportForm.value;
        const date = new Date().toISOString().substr(0, 10);
        if (form.startApproval && form.endApproval) {
            base += `_Start_${this.formatDate(form.startApproval)}_End_${this.formatDate(form.endApproval)}`;
        }

        if (form.program && form.program.programNameYear) {
            base += `_${form.program.programNameYear}`;
        }

        if (form.authNumber) {
            base += `_AuthNo_${form.authNumber}`;
        }

        base += `_${date}`;
        return base;

    }

    formatDate(date: Date): string {
        const formattedMonth = `0${date.getUTCMonth() + 1}`.slice(-2);
        const formattedDay = `0${date.getUTCDate()}`.slice(-2);
        const year = date.getUTCFullYear();

        return `${formattedMonth}_${formattedDay}_${year}`;
    }

}
